export type StammdatenTab = 'materialien' | 'leistungen' | 'vorlagen';

const TAB_TO_SLUG: Record<StammdatenTab, string> = {
  materialien: 'materialien',
  leistungen: 'leistungen',
  vorlagen: 'vorlagen',
};

const SLUG_TO_TAB = Object.fromEntries(
  (Object.entries(TAB_TO_SLUG) as [StammdatenTab, string][]).map(([tab, slug]) => [slug, tab]),
) as Record<string, StammdatenTab>;

export function stammdatenTabSlug(tab: StammdatenTab): string {
  return TAB_TO_SLUG[tab];
}

export function stammdatenTabFromSlug(slug: string | undefined): StammdatenTab | null {
  if (!slug) return null;
  return SLUG_TO_TAB[slug] ?? null;
}

export function stammdatenPath(instanceId: string, tab: StammdatenTab): string {
  return `/i/${encodeURIComponent(instanceId)}/stammdaten/${stammdatenTabSlug(tab)}`;
}

export function stammdatenDefaultPath(instanceId: string): string {
  return stammdatenPath(instanceId, 'materialien');
}

/** Query keys for the OMD product sidebar on Materialien. */
export const STAMMDATEN_OMD_SUPPLIER_PARAM = 'omd';
export const STAMMDATEN_OMD_ARTICLE_PARAM = 'article';

const OMD_SUPPLIER_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const OMD_ARTICLE_MAX_LEN = 120;

export type StammdatenOmdSelection = {
  supplierSlug: string;
  article: string;
};

export function parseStammdatenOmdSupplierParam(raw: string | null | undefined): string | null {
  const slug = String(raw ?? '').trim().toLowerCase();
  if (!slug || slug.length > 64 || !OMD_SUPPLIER_SLUG_RE.test(slug)) return null;
  return slug;
}

export function parseStammdatenOmdArticleParam(raw: string | null | undefined): string | null {
  const article = String(raw ?? '').trim();
  if (!article || article.length > OMD_ARTICLE_MAX_LEN) return null;
  if (/[\u0000-\u001f]/.test(article)) return null;
  return article;
}

/** Open OMD sidebar when both `omd` (supplier slug) and `article` are valid. */
export function parseStammdatenOmdSearch(
  search: URLSearchParams | { get: (name: string) => string | null },
): StammdatenOmdSelection | null {
  const supplierSlug = parseStammdatenOmdSupplierParam(search.get(STAMMDATEN_OMD_SUPPLIER_PARAM));
  const article = parseStammdatenOmdArticleParam(search.get(STAMMDATEN_OMD_ARTICLE_PARAM));
  if (!supplierSlug || !article) return null;
  return { supplierSlug, article };
}

export function setStammdatenOmdSearch(
  params: URLSearchParams,
  selection: StammdatenOmdSelection | null,
): URLSearchParams {
  if (!selection) {
    params.delete(STAMMDATEN_OMD_SUPPLIER_PARAM);
    params.delete(STAMMDATEN_OMD_ARTICLE_PARAM);
    return params;
  }
  params.set(STAMMDATEN_OMD_SUPPLIER_PARAM, selection.supplierSlug);
  params.set(STAMMDATEN_OMD_ARTICLE_PARAM, selection.article);
  return params;
}

/** Shareable Materialien URL with the OMD sidebar open for this article. */
export function stammdatenMaterialOmdPath(
  instanceId: string,
  selection: StammdatenOmdSelection,
): string {
  const params = setStammdatenOmdSearch(new URLSearchParams(), selection);
  return `${stammdatenPath(instanceId, 'materialien')}?${params.toString()}`;
}

export function stammdatenVorlageEditorPath(instanceId: string, vorlageId: string): string {
  return `/i/${encodeURIComponent(instanceId)}/stammdaten/vorlagen/${encodeURIComponent(vorlageId)}`;
}

/** True when path is vorlagen editor (…/stammdaten/vorlagen/:uuid). */
export function isStammdatenVorlageEditorPath(tabSlug: string | undefined, vorlageId: string | undefined): boolean {
  return tabSlug === 'vorlagen' && Boolean(vorlageId?.trim());
}
